import type { ActiveUsersForOrgUser } from '@/api/users/getActiveUsersForOrg';

export interface PersonForLabel {
  id: string;
  name: string;
  email?: string | null;
}

const toNameKey = (name: string) => name.trim().toLowerCase();

/**
 * Picker labels by person id. A name that more than one person in the list has gets their email
 * after it, so same-name people can be told apart; everyone else keeps the bare name.
 */
export const getPersonLabels = (people: readonly PersonForLabel[]): Map<string, string> => {
  const nameCounts = new Map<string, number>();
  for (const { name } of people) {
    const key = toNameKey(name);
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
  }

  return new Map(
    people.map(({ id, name, email }) => {
      const isSharedName = (nameCounts.get(toNameKey(name)) ?? 0) > 1;
      return [id, isSharedName && email ? `${name.trim()} (${email})` : name];
    }),
  );
};

/** `users/list-active` as picker options, with the email on shared names once the backend sends it. */
export const toActiveUserOptions = (users: readonly ActiveUsersForOrgUser[]): { label: string; value: string }[] => {
  const labels = getPersonLabels(
    users.map((user) => ({ id: user.fletchUserId, name: user.userName, email: user.email })),
  );

  return users.map((user) => ({ label: labels.get(user.fletchUserId) ?? user.userName, value: user.fletchUserId }));
};
